import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  CheckSquare,
  Calendar,
  Map as MapIcon,
  Printer,
  CheckCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import RoutePreviewHeader from '../components/RoutePreviewHeader';
import { AllRoutesMap } from '../components/AllRoutesMap';
import { RouteBoardPanel } from '../components/RouteBoardPanel';
import {
  TechnicianFilterPopover,
  type TechnicianFilterOption
} from '../components/TechnicianFilterPopover';
import useRoutePreview, { isStoredRoutePreview, TechnicianRoute } from '../hooks/useRoutePreview';
import useRouteBoard from '../hooks/useRouteBoard';
import useRoutePreviewPrinting from '../hooks/useRoutePreviewPrinting';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import {
  buildReorderedRouteData,
  applyDirectionsToRouteData,
  snapshotOptimized,
  restoreOptimizedRouteData
} from '../utils/routeBoardMapping';
import { optimizeRouteWithMapbox } from '../utils/mapboxOptimization';
import { recalcRouteAlongOrder } from '../utils/mapboxDirections';
import { Card, CardContent } from '@/components/ui/card';
import { SecurityBoundary } from '@/lib/security';
import { PermissionScope } from '@/types';
import { logDataAccess } from '@/lib/security/audit';
import { logUserActivity } from '@/lib/activity/userActivity';
import { useCurrentTenantId, useTenant } from '@/contexts/TenantContext';
import { format } from 'date-fns';
import { formatRouteDate } from '@/lib/supabase/technicianRoutes';
import { DatePicker } from '@/components/ui/date-picker';
import type {
  EarningsParams,
  RouteData,
  TechnicianRouteWithDetails
} from '@/types/technicianRoutes';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { useTechnicians } from '@/hooks/useTechnicians';
import { routeKeys, useRouteMutations, useTenantRouteSettings } from '../hooks/useTechnicianRoutes';
import { defaultOffsetForOems, resolveEarningsParams } from '../utils/earnings';
import type { RouteStop as MapRouteStop } from '../types';
import { cn } from '@/lib/utils';
import { CONTROL_DECK, SECTION_LABEL } from '@/components/ui/control-deck';

/**
 * Get today's date in the specified timezone
 * Falls back to local timezone if not specified or invalid
 */
const getTodayInTimezone = (timezone?: string): Date => {
  try {
    if (!timezone) {
      return new Date();
    }
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const dateStr = formatter.format(new Date()); // Returns YYYY-MM-DD format
    const parts = dateStr.split('-').map(Number);
    const year = parts[0] ?? new Date().getFullYear();
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return new Date(year, month - 1, day);
  } catch {
    return new Date();
  }
};

/**
 * Date-selection + filter control deck shared by the preview page's empty and
 * populated states.
 */
const RouteDateDeck: React.FC<{
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ selectedDate, onSelectDate, filters, actions }) => (
  <div className={cn('flex items-center justify-between gap-2 px-3 py-2 mb-4', CONTROL_DECK)}>
    <div className='flex flex-wrap items-center gap-4'>
      <div className='flex items-center gap-3'>
        <div className={SECTION_LABEL}>
          <Calendar className='h-4 w-4' />
          <span>Date</span>
        </div>
        <div className='w-[200px]'>
          <DatePicker
            value={selectedDate}
            onChange={date => date && onSelectDate(date)}
            placeholder='Select date'
          />
        </div>
      </div>
      {filters}
    </div>
    {actions && <div className='flex items-center gap-2'>{actions}</div>}
  </div>
);

/** Merge new route_data into a stored record, recomputing denormalized totals. */
const mergeRouteDataIntoRecord = (
  record: TechnicianRouteWithDetails,
  newRouteData: RouteData
): TechnicianRouteWithDetails => {
  const opt = newRouteData.optimization;
  return {
    ...record,
    route_data: newRouteData,
    total_stops: newRouteData.stops.length,
    total_recalls: newRouteData.stops.reduce(
      (s, st) => s + (st.recall_count || st.recalls.length),
      0
    ),
    ...(opt?.total_distance_miles != null && { total_distance_miles: opt.total_distance_miles }),
    ...(opt?.total_duration_minutes != null && {
      total_duration_minutes: opt.total_duration_minutes
    })
  };
};

const RoutePreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tenantId = useCurrentTenantId();
  const { currentTenant } = useTenant();
  const { syncToRouteTable } = useTechnicianAssignments();
  const { technicians } = useTechnicians();
  const { upsertRoute } = useRouteMutations();
  const { settings: routeSettings, updateSettings } = useTenantRouteSettings();
  const hasSyncedStaleData = useRef(false);

  const tenantTimezone = currentTenant?.time_zone;

  const [selectedDate, setSelectedDate] = useState<Date>(() => getTodayInTimezone(tenantTimezone));

  useEffect(() => {
    if (tenantTimezone) {
      setSelectedDate(getTodayInTimezone(tenantTimezone));
    }
  }, [tenantTimezone]);

  useEffect(() => {
    logUserActivity({ category: 'routing', action: 'view_route_preview' });
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────
  const routePreviewResult = useRoutePreview({ useStoredRoutes: true, routeDate: selectedDate });
  const isStoredMode = isStoredRoutePreview(routePreviewResult);
  const { dealers, loading, error, stats, isAuthenticated } = routePreviewResult;

  // Map-ready routes (coordinate-bearing) + raw records for persistence
  const { boardRoutes, recordsById } = useRouteBoard({
    routeDate: selectedDate,
    roster: technicians
  });

  // Only draft + in_progress routes appear on the preview page
  const dealerRoutes = useMemo<TechnicianRoute[]>(() => {
    if (!isStoredMode) return [];
    return routePreviewResult.routes.filter(
      route => route.status === 'draft' || route.status === 'in_progress'
    );
  }, [isStoredMode, routePreviewResult]);

  // ── Technician filter (persisted per-device per-tenant; adaptive default) ────
  const { selectedTechnicianIds, handleTechFilterChange } = useTechnicianFilter(
    tenantId,
    boardRoutes
  );

  const isVisible = useCallback(
    (technicianId: string) =>
      selectedTechnicianIds === null || selectedTechnicianIds.includes(technicianId),
    [selectedTechnicianIds]
  );

  const visibleBoardRoutes = useMemo(
    () => boardRoutes.filter(b => isVisible(b.technician_id)),
    [boardRoutes, isVisible]
  );
  const visibleDealerRoutes = useMemo(
    () => dealerRoutes.filter(r => isVisible(r.technicianId)),
    [dealerRoutes, isVisible]
  );

  // Technician filter options derived from technicians that have routes today
  const technicianOptions = useMemo<TechnicianFilterOption[]>(() => {
    const byId = new Map<string, TechnicianFilterOption>();
    boardRoutes.forEach(b => {
      if (!byId.has(b.technician_id)) {
        byId.set(b.technician_id, {
          id: b.technician_id,
          name: b.technicianName,
          color: b.route_color
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [boardRoutes]);

  // Live driving metrics from the map's Mapbox Directions render, keyed by routeId.
  // Used so the panel cards show the SAME distance/time as /routing/manual (which
  // also reads the Directions result), not the stored Optimization-API totals.
  const [liveMetrics, setLiveMetrics] = useState<
    Record<string, { distance: number; duration: number }>
  >({});

  const handleRouteMetrics = useCallback((routeId: string, distance: number, duration: number) => {
    setLiveMetrics(prev => {
      const existing = prev[routeId];
      // Skip no-op updates (avoids re-render churn from repeated routesfound events)
      if (
        existing &&
        Math.abs(existing.distance - distance) < 0.05 &&
        Math.abs(existing.duration - duration) < 0.5
      ) {
        return prev;
      }
      return { ...prev, [routeId]: { distance, duration } };
    });
  }, []);

  // Routes for the panel, with distance/time overridden by the live Directions
  // metrics when available (so cards match /routing/manual). The map keeps the
  // un-overridden routes to avoid re-running the routing control on metric updates.
  const panelRoutes = useMemo(
    () =>
      visibleBoardRoutes.map(route => {
        const live = liveMetrics[route.routeId];
        return live
          ? { ...route, total_distance: live.distance, total_duration: live.duration }
          : route;
      }),
    [visibleBoardRoutes, liveMetrics]
  );

  // ── Focus (selected technician on the map) ──────────────────────────────────
  const [focusedTechnicianId, setFocusedTechnicianId] = useState<string | null>(null);
  useEffect(() => {
    // Clear focus if the focused technician is filtered out
    if (
      focusedTechnicianId &&
      !visibleBoardRoutes.some(b => b.technician_id === focusedTechnicianId)
    ) {
      setFocusedTechnicianId(null);
    }
  }, [focusedTechnicianId, visibleBoardRoutes]);

  // ── Panel expansion ─────────────────────────────────────────────────────────
  const [expandedRouteIds, setExpandedRouteIds] = useState<Set<string>>(new Set());
  const toggleRouteExpanded = (routeId: string) => {
    setExpandedRouteIds(prev => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  };

  // Recalculate stats based on visible routes
  const filteredStats = useMemo(() => {
    return {
      ...stats,
      totalRoutes: visibleDealerRoutes.length,
      totalDealers: visibleDealerRoutes.reduce((sum, r) => sum + r.stats.totalDealers, 0),
      totalRecalls: visibleDealerRoutes.reduce((sum, r) => sum + r.stats.totalRecalls, 0),
      totalVehicles: visibleDealerRoutes.reduce((sum, r) => sum + r.stats.totalVehicles, 0),
      estimatedDuration: visibleDealerRoutes.reduce((sum, r) => sum + r.stats.estimatedDuration, 0)
    };
  }, [stats, visibleDealerRoutes]);

  // Auto-refresh routes with stale/missing data (unchanged behavior)
  useEffect(() => {
    if (loading || dealerRoutes.length === 0 || hasSyncedStaleData.current) return;
    const hasStaleData = dealerRoutes.some(route =>
      route.dealers.some(dealer => {
        const hasMissingVehicleInfo = dealer.vehicles.some(
          v => v.year === 'Unknown' && v.vin && v.vin.length > 0
        );
        const hasMissingDealerAddress = dealer.city === 'Unknown';
        const hasMissingPhone = !dealer.phone;
        return hasMissingVehicleInfo || hasMissingDealerAddress || hasMissingPhone;
      })
    );
    if (hasStaleData) {
      hasSyncedStaleData.current = true;
      const syncPromises = dealerRoutes.map(route =>
        syncToRouteTable(route.technicianId, route.routeDate)
      );
      Promise.all(syncPromises).then(() => {
        queryClient.invalidateQueries({ queryKey: routeKeys.all });
      });
    }
  }, [loading, dealerRoutes, syncToRouteTable, queryClient]);

  const [reoptimizingRouteId, setReoptimizingRouteId] = useState<string | null>(null);

  // Print actions + printed-status tracking (extracted hook)
  const {
    printStatus,
    isPrintingAll,
    allPrinted,
    handlePrintDealerById,
    handlePrintRouteById,
    handlePrintAll
  } = useRoutePreviewPrinting({ dealerRoutes, visibleDealerRoutes, tenantId });

  // Update URL when date changes
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('date', formatRouteDate(selectedDate));
    setSearchParams(params, { replace: true });
  }, [selectedDate, setSearchParams]);

  // ── Earnings params + fallback base location from tenant settings ───────────
  const earningsParams = useMemo(
    () =>
      resolveEarningsParams(
        routeSettings?.earnings_params,
        defaultOffsetForOems(currentTenant?.oems, currentTenant?.oem)
      ),
    [routeSettings, currentTenant]
  );
  const fallbackBaseLocation = useMemo(() => {
    const loc = routeSettings?.default_start_location;
    if (loc && loc.lat && loc.lng && !isNaN(loc.lat) && !isNaN(loc.lng)) {
      return { lat: loc.lat, lng: loc.lng };
    }
    return null;
  }, [routeSettings]);

  const handleSaveEarningsParams = useCallback(
    (params: EarningsParams) => {
      // Optimistic via React Query cache; the mutation persists + toasts.
      updateSettings.mutate({ ...(routeSettings || {}), earnings_params: params });
    },
    [routeSettings, updateSettings]
  );

  // ── Persist a route's reordered/optimized stops (optimistic at cache layer) ──
  const applyRouteDataToCache = useCallback(
    (routeId: string, newRouteData: RouteData) => {
      queryClient.setQueriesData<TechnicianRouteWithDetails[] | undefined>(
        { queryKey: routeKeys.lists() },
        old => {
          if (!old) return old;
          return old.map(record =>
            record.id === routeId ? mergeRouteDataIntoRecord(record, newRouteData) : record
          );
        }
      );
    },
    [queryClient]
  );

  const persistRouteData = useCallback(
    (record: TechnicianRouteWithDetails, newRouteData: RouteData, previous: RouteData) => {
      applyRouteDataToCache(record.id, newRouteData);
      upsertRoute.mutate(
        {
          technicianId: record.technician_id,
          routeDate: record.route_date,
          routeData: newRouteData,
          status: record.status
        },
        {
          onError: () => {
            // Roll back the optimistic cache write
            applyRouteDataToCache(record.id, previous);
          }
        }
      );
    },
    [applyRouteDataToCache, upsertRoute]
  );

  const handleReorderStops = useCallback(
    (routeId: string, newStops: MapRouteStop[]) => {
      const record = recordsById[routeId];
      if (!record) return;
      const previous = record.route_data;
      const ts = new Date().toISOString();

      // 1. INSTANT: persist the new order. On the first manual edit, snapshot the
      //    current optimized state so the user can revert to it later.
      const reordered = buildReorderedRouteData(previous, newStops, ts, 'manual');
      const orderOnly: RouteData = previous.original_optimized
        ? reordered
        : { ...reordered, original_optimized: snapshotOptimized(previous) };
      persistRouteData(record, orderOnly, previous);
      toast({
        title: 'Route updated',
        description: `New stop order saved for ${record.technician_name}.`,
        variant: 'success'
      });
      logUserActivity({
        category: 'routing',
        action: 'reorder_route',
        entityType: 'route',
        entityId: routeId,
        entityName: record.technician_name,
        metadata: { technician_id: record.technician_id, stop_count: newStops.length }
      });

      // 2. BACKGROUND: recompute real driving distance/time/geometry ALONG the
      //    chosen order so the panel, stored record, and printout all agree.
      const board = boardRoutes.find(b => b.routeId === routeId);
      const base = board?.startLocation || fallbackBaseLocation;
      if (!base) return;
      recalcRouteAlongOrder(orderOnly.stops, base)
        .then(result => {
          const withMetrics = applyDirectionsToRouteData(
            orderOnly,
            result,
            'manual',
            new Date().toISOString()
          );
          persistRouteData(record, withMetrics, orderOnly);
        })
        .catch(() => {
          // Keep the order-only state; numbers simply aren't refreshed.
        });
    },
    [recordsById, boardRoutes, fallbackBaseLocation, persistRouteData, toast]
  );

  const handleReoptimize = useCallback(
    async (routeId: string) => {
      const record = recordsById[routeId];
      const board = boardRoutes.find(b => b.routeId === routeId);
      if (!record || !board) return;
      const base = board.startLocation || fallbackBaseLocation;
      if (!base) {
        toast({
          title: 'No start location',
          description: 'Set a route start location before optimizing.',
          variant: 'destructive'
        });
        return;
      }
      setReoptimizingRouteId(routeId);
      try {
        const previous = record.route_data;
        // Optimization API decides the best ORDER. When it succeeds it also
        // returns totals + geometry; when it falls back (nearest-neighbor) those
        // are empty, so fetch real numbers/geometry along the new order.
        const opt = await optimizeRouteWithMapbox(previous.stops, base);
        const metrics =
          opt.totalDistanceMiles > 0 ? opt : await recalcRouteAlongOrder(opt.stops, base);
        const optimizedAt = new Date().toISOString();
        const applied = applyDirectionsToRouteData(previous, metrics, opt.algorithm, optimizedAt);
        // Refresh the revert snapshot to this new optimal result.
        const newRouteData: RouteData = {
          ...applied,
          original_optimized: snapshotOptimized(applied)
        };
        persistRouteData(record, newRouteData, previous);
        toast({
          title: 'Route re-optimized',
          description: `Optimal stop order applied for ${record.technician_name}.`,
          variant: 'success'
        });
      } catch {
        toast({
          title: 'Optimization failed',
          description: 'Could not re-optimize this route. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setReoptimizingRouteId(null);
      }
    },
    [recordsById, boardRoutes, fallbackBaseLocation, persistRouteData, toast]
  );

  const handleRevertOptimized = useCallback(
    (routeId: string) => {
      const record = recordsById[routeId];
      if (!record) return;
      const previous = record.route_data;
      const reverted = restoreOptimizedRouteData(previous);
      if (!reverted) return; // no snapshot to revert to
      persistRouteData(record, reverted, previous);
      toast({
        title: 'Reverted to optimized route',
        description: `Optimized stop order restored for ${record.technician_name}.`,
        variant: 'success'
      });
      logUserActivity({
        category: 'routing',
        action: 'reorder_route',
        entityType: 'route',
        entityId: routeId,
        entityName: record.technician_name,
        metadata: { technician_id: record.technician_id, reverted_to_optimized: true }
      });
    },
    [recordsById, persistRouteData, toast]
  );

  // Audit: log data access when dealers load
  useEffect(() => {
    if (dealers.length > 0 && tenantId) {
      logDataAccess({
        resource: 'route_preview',
        action: 'view',
        count: dealers.length,
        metadata: { dealerCount: dealers.length, totalRecalls: stats.totalRecalls, tenantId }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealers.length]);

  const technicianFilter = (
    <TechnicianFilterPopover
      technicians={technicianOptions}
      selectedIds={selectedTechnicianIds}
      onChange={handleTechFilterChange}
    />
  );

  const routeCount = visibleDealerRoutes.length;
  const printAllLabel = isPrintingAll
    ? 'Printing...'
    : `Print All (${routeCount} ${routeCount === 1 ? 'Route' : 'Routes'})`;

  // ── States ──────────────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className='p-6 text-center'>
          <AlertTriangle className='w-12 h-12 text-amber-500 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-foreground mb-2'>Authentication Required</h3>
          <p className='text-muted-foreground'>Please log in to access route preview.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className='container mx-auto p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-32 bg-muted rounded-lg' />
          <div className='h-64 bg-muted rounded-lg' />
          <div className='h-64 bg-muted rounded-lg' />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className='max-w-2xl mx-auto mt-8'>
        <CardContent className='p-6 text-center'>
          <AlertTriangle className='w-12 h-12 text-red-500 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-foreground mb-2'>Error Loading Routes</h3>
          <p className='text-muted-foreground'>{error}</p>
          <Button onClick={() => navigate('/recall-map')} className='mt-4'>
            Back to Recall Map
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (dealerRoutes.length === 0) {
    return (
      <SecurityBoundary
        requiredPermissions={[{ resource: 'recalls', action: 'read', scope: PermissionScope.SELF }]}
        auditPageAccess='route-preview'
        tenantIsolation={true}
      >
        <div className='h-full flex flex-col overflow-hidden'>
          <div className='flex-shrink-0 border-b bg-background pt-2 px-4 lg:px-6 pb-4'>
            <RouteDateDeck selectedDate={selectedDate} onSelectDate={setSelectedDate} />
          </div>
          <div className='flex-1 flex items-center justify-center'>
            <Card className='max-w-2xl'>
              <CardContent className='p-6 text-center'>
                <CheckSquare className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
                <h3 className='text-lg font-medium text-foreground mb-2'>No Routes Found</h3>
                <p className='text-muted-foreground mb-4'>
                  No routes found for {format(selectedDate, 'MMMM d, yyyy')}. Routes are created
                  when recalls are assigned to technicians.
                </p>
                <div className='flex gap-2 justify-center'>
                  <Button onClick={() => navigate('/recall-map')}>Go to Recall Map</Button>
                  <Button variant='outline' onClick={() => navigate('/routing/manual')}>
                    Assign Technicians
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </SecurityBoundary>
    );
  }

  return (
    <SecurityBoundary
      requiredPermissions={[{ resource: 'recalls', action: 'read', scope: PermissionScope.SELF }]}
      auditPageAccess='route-preview'
      tenantIsolation={true}
    >
      <div className='h-full flex flex-col overflow-hidden'>
        {/* Fixed Header Section */}
        <div className='flex-shrink-0 border-b bg-background pt-2 px-4 lg:px-6 pb-4'>
          {/* Page title removed — the left nav labels the page */}

          <RouteDateDeck
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            filters={technicianFilter}
            actions={
              <>
                {allPrinted && (
                  <span className='flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400'>
                    <CheckCircle className='h-3.5 w-3.5' />
                    All printed
                  </span>
                )}
                <Button
                  size='sm'
                  className='h-8 gap-2 px-3 text-xs'
                  onClick={handlePrintAll}
                  disabled={isPrintingAll || visibleDealerRoutes.length === 0}
                >
                  <Printer className='h-4 w-4' />
                  {printAllLabel}
                </Button>
              </>
            }
          />

          <RoutePreviewHeader
            stats={filteredStats}
            earningsParams={earningsParams}
            onSaveEarningsParams={handleSaveEarningsParams}
          />
        </div>

        {/* Split: Map + Stop Panel */}
        <div className='flex-1 flex flex-col lg:flex-row min-h-0'>
          <div className='flex-1 min-h-[320px] lg:min-h-0 relative'>
            {visibleBoardRoutes.length > 0 ? (
              <AllRoutesMap
                routes={visibleBoardRoutes}
                focusedTechnicianId={focusedTechnicianId}
                fallbackBaseLocation={fallbackBaseLocation}
                onRouteMetrics={handleRouteMetrics}
              />
            ) : (
              <div className='h-full flex items-center justify-center bg-muted/20'>
                <div className='text-center'>
                  <MapIcon className='h-12 w-12 text-muted-foreground/50 mx-auto mb-3' />
                  <p className='text-sm text-muted-foreground'>
                    No technicians selected. Choose technicians to see their routes.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div
            className={cn(
              'w-full lg:w-[400px] flex-shrink-0 overflow-y-auto p-3 bg-background',
              'border-t lg:border-t-0 lg:border-l'
            )}
          >
            <RouteBoardPanel
              routes={panelRoutes}
              focusedTechnicianId={focusedTechnicianId}
              onFocusTechnician={setFocusedTechnicianId}
              expandedRouteIds={expandedRouteIds}
              onToggleExpanded={toggleRouteExpanded}
              onReorderStops={handleReorderStops}
              onReoptimize={handleReoptimize}
              onRevertOptimized={handleRevertOptimized}
              onPrintRoute={handlePrintRouteById}
              onPrintDealer={handlePrintDealerById}
              printStatus={printStatus}
              reoptimizingRouteId={reoptimizingRouteId}
            />
          </div>
        </div>
      </div>
    </SecurityBoundary>
  );
};

export default RoutePreviewPage;
